import { Injectable } from '@angular/core';
import { BaseTag, createTagsPlugin, Tag, TagsApi } from '@algolia/autocomplete-plugin-tags';
import { MenuItem } from '@mode/shared/ui';
import { groupBy } from 'lodash';
import { BaseItem } from '@algolia/autocomplete-core';
import { AutocompletePlugin } from '@algolia/autocomplete-shared';

export type FacetTagProps = { facet: string; type?: 'filter' | 'attr' };
export type FacetTags = Array<BaseTag<FacetTagProps>>;

@Injectable({
  providedIn: 'root',
})
export class AutocompleteTagsService {
  public algoliaTagsPlugin!: AutocompletePlugin<Tag<FacetTagProps>, TagsApi<FacetTagProps>>;

  constructor() {
    this.initTagsPlugin();
  }

  private initTagsPlugin(): void {
    this.algoliaTagsPlugin = createTagsPlugin<BaseItem, FacetTagProps>({
      // don't add tags to list of sources, we'll handle tags ui in render method
      transformSource: () => undefined,
    });
  }

  public resetTagsPlugin(): void {
    // preserve current tag state
    const tags = this.algoliaTagsPlugin.data?.tags;
    // initialize new tags plugin instance and clear tags
    this.initTagsPlugin();

    if (tags) {
      // reset tags to previous state
      this.algoliaTagsPlugin.data?.setTags(tags);
    }
  }

  public createTag(
    item: MenuItem<string | boolean>,
    facet: string,
    type: FacetTagProps['type'] = 'filter'
  ): BaseTag<FacetTagProps> {
    return {
      label: `${item.value}`,
      facet,
      type,
    };
  }

  public tagsByFacet(tags: any) {
    return groupBy(tags, (tag: any) => tag.facet);
  }

  public getTag(facet: string, label: string, tags: any) {
    return this.tagsByFacet(tags)[facet]?.find((t: any) => t.label === label);
  }

  public getTagsByType(tags: FacetTags, type: FacetTagProps['type']): FacetTags {
    return tags.filter((tag) => tag.type === type);
  }

  public addTags(tags: { label: string; facet: string }[], tagData: TagsApi) {
    tagData.addTags(tags);
  }

  public setTags(tags: FacetTags) {
    this.algoliaTagsPlugin.data?.setTags(tags);
  }
}
